import Papa from 'papaparse';

const DEFAULT_CSV_PARSE_OPTIONS = {
  skipEmptyLines: false,
  dynamicTyping: false
};

function getFileExtension(fileName = '') {
  const match = String(fileName).toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read the file as text.'));
    reader.readAsText(file);
  });
}

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read the file as binary data.'));
    reader.readAsArrayBuffer(file);
  });
}

function parseDelimitedText(text, { delimiter = '', ...overrides } = {}) {
  return new Promise((resolve, reject) => {
    Papa.parse(text, {
      ...DEFAULT_CSV_PARSE_OPTIONS,
      ...overrides,
      delimiter,
      complete: (result) => {
        const firstError = Array.isArray(result?.errors) ? result.errors.find(Boolean) : null;
        if (firstError) {
          reject(new Error(firstError.message || 'Could not parse the delimited file.'));
          return;
        }

        resolve(Array.isArray(result?.data) ? result.data : []);
      },
      error: (error) => {
        reject(error instanceof Error ? error : new Error('Could not parse the delimited file.'));
      }
    });
  });
}

async function parseDelimitedFile(file, extension) {
  const text = await readFileAsText(file);
  const delimiter = extension === 'tsv' ? '\t' : '';
  const rows = await parseDelimitedText(text, { delimiter });

  return {
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || '',
    extension,
    sheets: [
      {
        name: 'Sheet 1',
        rows
      }
    ]
  };
}

async function parseSpreadsheetFile(file, extension) {
  let XLSX;

  try {
    XLSX = await import('xlsx');
  } catch (error) {
    throw new Error('Spreadsheet import requires the xlsx package. Install it before enabling spreadsheet uploads.');
  }

  const buffer = await readFileAsArrayBuffer(file);
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetNames = Array.isArray(workbook?.SheetNames) ? workbook.SheetNames : [];

  const sheets = sheetNames.map((sheetName) => {
    const worksheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      raw: false,
      defval: ''
    });

    return {
      name: sheetName,
      rows: Array.isArray(rows) ? rows : []
    };
  });

  return {
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || '',
    extension,
    sheets
  };
}

export async function loadTabularFile(file) {
  if (!(file instanceof File)) {
    throw new Error('A file is required.');
  }

  const extension = getFileExtension(file.name);
  if (!extension) {
    throw new Error('The file type could not be determined from the file name.');
  }

  if (['csv', 'tsv', 'txt'].includes(extension)) {
    return parseDelimitedFile(file, extension);
  }

  if (['xlsx', 'xls'].includes(extension)) {
    return parseSpreadsheetFile(file, extension);
  }

  throw new Error('Unsupported file type. Use CSV, TSV, TXT, XLSX, or XLS.');
}

export function pickDefaultSheet(tabularFile) {
  const sheets = Array.isArray(tabularFile?.sheets) ? tabularFile.sheets : [];
  const firstNonEmpty = sheets.find((sheet) => Array.isArray(sheet?.rows) && sheet.rows.some((row) => Array.isArray(row) && row.some((cell) => String(cell || '').trim())));
  return firstNonEmpty || sheets[0] || null;
}
